import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useToast } from '../components/ui/use-toast';
import { useGlobalRenderUpdates } from '../hooks/useSocket';
import { connectSocket, RenderUpdateEvent } from '../lib/socket';

export interface PendingRender {
  id: string;
  roomId: string;
  quoteId: string;
  roomName: string;
  quoteName: string;
  startedAt: Date;
  status: 'processing' | 'completed' | 'failed';
  imageUrl?: string;
  error?: string;
}

interface RenderProgressContextValue {
  pendingRenders: PendingRender[];
  addPendingRender: (render: Omit<PendingRender, 'startedAt' | 'status'>) => void;
  removePendingRender: (id: string) => void;
  clearCompletedRenders: () => void;
  processingCount: number;
}

const RenderProgressContext = createContext<RenderProgressContextValue | null>(null);

export function useRenderProgress() {
  const context = useContext(RenderProgressContext);
  if (!context) {
    throw new Error('useRenderProgress must be used within RenderProgressProvider');
  }
  return context;
}

interface RenderProgressProviderProps {
  children: React.ReactNode;
}

export function RenderProgressProvider({ children }: RenderProgressProviderProps) {
  const [pendingRenders, setPendingRenders] = useState<PendingRender[]>([]);
  const { toast } = useToast();

  // Initialize socket connection on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      connectSocket(token);
    }
  }, []);

  // Handle render updates from WebSocket
  const handleRenderUpdate = useCallback((data: RenderUpdateEvent) => {
    setPendingRenders((prev) => {
      const existing = prev.find((r) => r.id === data.renderId);

      if (data.status === 'completed' || data.status === 'failed') {
        // Show toast notification for completed/failed renders
        if (existing || data.status === 'completed') {
          const title = data.status === 'completed' ? 'Render Complete' : 'Render Failed';
          const description = data.status === 'completed'
            ? `${data.roomName || 'Room'} render for ${data.quoteName || 'quote'} is ready.`
            : `${data.roomName || 'Room'} render failed: ${data.error || 'Unknown error'}`;

          toast({
            title,
            description,
            variant: data.status === 'failed' ? 'destructive' : 'default',
            duration: 5000,
          });
        }

        // Update status or add to list
        if (existing) {
          return prev.map((r) =>
            r.id === data.renderId
              ? { ...r, status: data.status, imageUrl: data.imageUrl, error: data.error }
              : r
          );
        }

        // If not in our list, don't add it (it wasn't started from this session)
        return prev;
      }

      // For processing status, update if exists
      if (existing) {
        return prev.map((r) =>
          r.id === data.renderId ? { ...r, status: data.status } : r
        );
      }

      return prev;
    });
  }, [toast]);

  // Subscribe to global render updates
  useGlobalRenderUpdates(handleRenderUpdate);

  const addPendingRender = useCallback((render: Omit<PendingRender, 'startedAt' | 'status'>) => {
    setPendingRenders((prev) => {
      // Don't add duplicates
      if (prev.some((r) => r.id === render.id)) {
        return prev;
      }
      return [
        ...prev,
        {
          ...render,
          startedAt: new Date(),
          status: 'processing',
        },
      ];
    });
  }, []);

  const removePendingRender = useCallback((id: string) => {
    setPendingRenders((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const clearCompletedRenders = useCallback(() => {
    setPendingRenders((prev) => prev.filter((r) => r.status === 'processing'));
  }, []);

  const processingCount = pendingRenders.filter((r) => r.status === 'processing').length;

  return (
    <RenderProgressContext.Provider
      value={{
        pendingRenders,
        addPendingRender,
        removePendingRender,
        clearCompletedRenders,
        processingCount,
      }}
    >
      {children}
    </RenderProgressContext.Provider>
  );
}
